// ABOUTME: Translated labels for every known job type, plus the launch-name alias map.
// ABOUTME: Shared by the job list, the overview title and the manual-launch picklist.

import { defineMessages, MessageDescriptor, useIntl } from 'react-intl';

// Labels mirror the job catalogue documented in docs/user/jobs.md, so docs and UI stay
// aligned. defineMessages is required here: formatjs cannot extract ids from a
// formatMessage(variable) call, only from a literal descriptor.
const messages = defineMessages({
    // Import
    import: { id: 'job.type.import', defaultMessage: 'Hierarchy import' },
    CatImport: { id: 'job.type.CatImport', defaultMessage: 'CAT import' },
    'cat-import': { id: 'job.type.cat-import', defaultMessage: 'CAT import from folder' },
    ObjectSchemaImportJob: {
        id: 'job.type.ObjectSchemaImportJob',
        defaultMessage: 'Object schema import',
    },
    'boomi-importer': { id: 'job.type.boomi-importer', defaultMessage: 'Boomi importer' },
    'cgt-import-sap-hierarchy': {
        id: 'job.type.cgt-import-sap-hierarchy',
        defaultMessage: 'SAP hierarchy import',
    },
    'cgt-import-ricambi': {
        id: 'job.type.cgt-import-ricambi',
        defaultMessage: 'CGT spare parts import',
    },
    'cgt-import-sap-mm-dispo': {
        id: 'job.type.cgt-import-sap-mm-dispo',
        defaultMessage: 'SAP MM Dispo import',
    },
    configitImport: { id: 'job.type.configitImport', defaultMessage: 'Configit import' },
    importBom: { id: 'job.type.importBom', defaultMessage: 'BOM import' },
    'import-idp-users': {
        id: 'job.type.import-idp-users',
        defaultMessage: 'Keycloak user import',
    },
    importVariantPricing: {
        id: 'job.type.importVariantPricing',
        defaultMessage: 'Variant pricing import',
    },

    // Export
    ExportXlsx: { id: 'job.type.ExportXlsx', defaultMessage: 'Excel export' },
    'coolsales-exporter': { id: 'job.type.coolsales-exporter', defaultMessage: 'Coolsales export' },
    ExportHistory: { id: 'job.type.ExportHistory', defaultMessage: 'History export' },
    'cpq-list-export': {
        id: 'job.type.cpq-list-export',
        defaultMessage: 'Offers/orders list export',
    },
    ObjectSchemaExportJob: {
        id: 'job.type.ObjectSchemaExportJob',
        defaultMessage: 'Object schema export',
    },
    exportCommercialItems: {
        id: 'job.type.exportCommercialItems',
        defaultMessage: 'Commercial items export',
    },
    exportCommonData: { id: 'job.type.exportCommonData', defaultMessage: 'Common data export' },
    exportGls: { id: 'job.type.exportGls', defaultMessage: 'GL export' },
    'export-trasversal-gls': {
        id: 'job.type.export-trasversal-gls',
        defaultMessage: 'Transversal GL export',
    },

    // Hierarchy operations
    'publish-hierarchy': {
        id: 'job.type.publish-hierarchy',
        defaultMessage: 'Hierarchy publish',
    },
    'duplicate-hierarchy': {
        id: 'job.type.duplicate-hierarchy',
        defaultMessage: 'Hierarchy duplication',
    },
    'duplicate-node': { id: 'job.type.duplicate-node', defaultMessage: 'Node duplication' },
    'auto-translate-hierarchy': {
        id: 'job.type.auto-translate-hierarchy',
        defaultMessage: 'Hierarchy auto-translation',
    },
    'restart-translation': {
        id: 'job.type.restart-translation',
        defaultMessage: 'Translation restart',
    },
    'propagate-node-permissions': {
        id: 'job.type.propagate-node-permissions',
        defaultMessage: 'Permission propagation',
    },
    'find-replace-scan': {
        id: 'job.type.find-replace-scan',
        defaultMessage: 'Bulk change scan',
    },
    'find-replace-apply': {
        id: 'job.type.find-replace-apply',
        defaultMessage: 'Bulk change apply',
    },
    'bulk-change-rule': {
        id: 'job.type.bulk-change-rule',
        defaultMessage: 'Bulk change rule',
    },
    'publish-family': { id: 'job.type.publish-family', defaultMessage: 'Family publish' },
    'close-family': { id: 'job.type.close-family', defaultMessage: 'Family closure' },
    'reopen-family': { id: 'job.type.reopen-family', defaultMessage: 'Family reopening' },
    Publish: { id: 'job.type.Publish', defaultMessage: 'Publish' },
    pushBomPackage: { id: 'job.type.pushBomPackage', defaultMessage: 'BOM package push' },

    // Data validation & quality
    massiveCheck: { id: 'job.type.massiveCheck', defaultMessage: 'Massive check' },
    coolsalesMisalignedOffers: {
        id: 'job.type.coolsalesMisalignedOffers',
        defaultMessage: 'Misaligned Coolsales offers',
    },
    AlignPrices: { id: 'job.type.AlignPrices', defaultMessage: 'Price alignment' },
    'zeus-coolsales-diff': {
        id: 'job.type.zeus-coolsales-diff',
        defaultMessage: 'Zeus/Coolsales comparison',
    },

    // Maintenance & configuration
    'cleanup-eventstore': {
        id: 'job.type.cleanup-eventstore',
        defaultMessage: 'Event store cleanup',
    },
    'cleanup-external-call-log': {
        id: 'job.type.cleanup-external-call-log',
        defaultMessage: 'External call log cleanup',
    },
    'cleanup-user-analytics': {
        id: 'job.type.cleanup-user-analytics',
        defaultMessage: 'User analytics cleanup',
    },
    'generate-snapshot': {
        id: 'job.type.generate-snapshot',
        defaultMessage: 'Snapshot generation',
    },
    'expire-price-lists': {
        id: 'job.type.expire-price-lists',
        defaultMessage: 'Price list expiry',
    },
    pboDataMigration: { id: 'job.type.pboDataMigration', defaultMessage: 'PBO data migration' },
    migrateDecisionTableCells: {
        id: 'job.type.migrateDecisionTableCells',
        defaultMessage: 'Decision table cell migration',
    },
    'backfill-content-graph': {
        id: 'job.type.backfill-content-graph',
        defaultMessage: 'Content graph backfill',
    },
    'backfill-published-content-graph': {
        id: 'job.type.backfill-published-content-graph',
        defaultMessage: 'Published content graph backfill',
    },
    'align-dealership-sharing': {
        id: 'job.type.align-dealership-sharing',
        defaultMessage: 'Dealership sharing alignment',
    },
    'align-market-stamps': {
        id: 'job.type.align-market-stamps',
        defaultMessage: 'Market stamp alignment',
    },
    'agent-runner': {
        id: 'job.type.agent-runner',
        defaultMessage: 'Agent run',
    },
    backfillMediaThumbnails: {
        id: 'job.type.backfillMediaThumbnails',
        defaultMessage: 'Media thumbnail backfill',
    },
    migrateLegacyMediaFields: {
        id: 'job.type.migrateLegacyMediaFields',
        defaultMessage: 'Legacy media fields migration',
    },
    'compute-model-solution-space': {
        id: 'job.type.compute-model-solution-space',
        defaultMessage: 'Model option filters computation',
    },
    'couch-snapshot': { id: 'job.type.couch-snapshot', defaultMessage: 'CouchDB snapshot' },
});

export const JOB_TYPE_LABELS: Record<string, MessageDescriptor> = messages;

/**
 * The manual-launch picklist and the create route use launch names that do not always match
 * the persisted job type — 'catImport' launches a job stored as 'CatImport'.
 */
const JOB_LAUNCH_NAME_TO_TYPE: Record<string, string> = {
    catImport: 'CatImport',
};

export const resolveJobType = (nameOrType: string) =>
    JOB_LAUNCH_NAME_TO_TYPE[nameOrType] ?? nameOrType;

/**
 * Returns a translated job label, falling back to the raw string for types that have no
 * label yet — a job type added on the server must not flood the console with
 * MISSING_TRANSLATION warnings.
 */
export function useJobTypeLabel() {
    const intl = useIntl();
    return (nameOrType: string) => {
        const label = JOB_TYPE_LABELS[resolveJobType(nameOrType)];
        return label ? intl.formatMessage(label) : nameOrType;
    };
}
